import { LogIn } from 'lucide-react'
import { type FormEvent, useState } from 'react'

import {
  useForgotPassword,
  useHealth,
  useLogin,
  useTotpVerify,
  useWebauthnAuthenticateOptions,
  useWebauthnAuthenticateVerify,
} from '../api/auth'
import { ApiError } from '../api/client'
import { BrandLogo } from '../components/BrandLogo'
import { PasswordField } from '../components/PasswordField'
import { ThemeToggle } from '../components/ThemeToggle'
import { TurnstileWidget } from '../components/TurnstileWidget'
import { getWebauthnCredential, isWebauthnSupported } from '../lib/webauthn'

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''

const FEATURE_PILLS = [
  'Backtesting multi-estrategia',
  'Múltiples exchanges y timeframes',
  'Bots en vivo, con control de riesgo',
]

/** Velas japonesas decorativas al pie del panel de login — sin datos
 *  reales detrás, un pump-and-consolidate a mano (mismo tipo de forma que
 *  un gráfico real: base, arranque, techo con mechas largas, pullback,
 *  otra base, cierre en alza) en vez de un zigzag parejo. `o`/`c`/`h`/`l`
 *  son posiciones en el eje Y de SVG — a menor valor, más arriba en
 *  pantalla = precio más alto — no unidades de precio reales. */
type Candle = { o: number; c: number; h: number; l: number }

const CANDLES: Candle[] = [
  { o: 280, c: 270, h: 260, l: 290 },
  { o: 270, c: 282, h: 262, l: 292 },
  { o: 282, c: 268, h: 258, l: 294 },
  { o: 268, c: 278, h: 260, l: 288 },
  { o: 278, c: 260, h: 250, l: 286 },
  { o: 260, c: 272, h: 252, l: 280 },
  { o: 272, c: 255, h: 245, l: 282 },
  { o: 255, c: 230, h: 222, l: 260 },
  { o: 230, c: 200, h: 190, l: 236 },
  { o: 200, c: 175, h: 165, l: 206 },
  { o: 175, c: 145, h: 135, l: 182 },
  { o: 145, c: 155, h: 125, l: 162 },
  { o: 155, c: 110, h: 98, l: 160 },
  { o: 110, c: 120, h: 95, l: 128 },
  { o: 120, c: 75, h: 60, l: 126 },
  { o: 75, c: 85, h: 55, l: 95 },
  { o: 85, c: 45, h: 30, l: 92 },
  { o: 45, c: 60, h: 28, l: 70 },
  { o: 60, c: 35, h: 22, l: 68 },
  { o: 35, c: 55, h: 25, l: 62 },
  { o: 55, c: 90, h: 48, l: 98 },
  { o: 90, c: 115, h: 82, l: 122 },
  { o: 115, c: 105, h: 95, l: 125 },
  { o: 105, c: 130, h: 98, l: 138 },
  { o: 130, c: 120, h: 110, l: 140 },
  { o: 120, c: 145, h: 112, l: 152 },
  { o: 145, c: 135, h: 125, l: 152 },
  { o: 135, c: 150, h: 125, l: 160 },
  { o: 150, c: 125, h: 115, l: 158 },
  { o: 125, c: 95, h: 82, l: 132 },
  { o: 95, c: 60, h: 48, l: 102 },
  { o: 60, c: 30, h: 18, l: 68 },
]

const CANDLE_SLOT = 45
const CANDLE_BODY_WIDTH = 20
const CANDLE_CHART_WIDTH = CANDLES.length * CANDLE_SLOT
const CANDLE_CHART_HEIGHT = 320

/** Promedio móvil simple sobre los cierres — sin ventana completa todavía
 *  (los primeros `window - 1` valores), promedia lo que hay, como
 *  cualquier librería de charting hace en el arranque de la serie. */
function movingAverage(values: number[], window: number): number[] {
  return values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - window + 1), i + 1)
    return slice.reduce((sum, value) => sum + value, 0) / slice.length
  })
}

function linePath(points: { x: number; y: number }[]): string {
  return points.map((point, i) => `${i === 0 ? 'M' : 'L'}${point.x},${point.y.toFixed(1)}`).join(' ')
}

// `ApiError` trae un mensaje real del backend (credenciales inválidas, 2FA
// vencido, etc.) — cualquier otro error es que `fetch` ni llegó a completar
// (sin red, CORS, el servidor caído), y su `.message` es algo técnico tipo
// "Failed to fetch" que un login fallido de verdad no debería confundir con
// una contraseña incorrecta.
function loginErrorMessage(error: Error): string {
  if (error instanceof ApiError) return error.message
  return 'No se pudo conectar con el servidor. Revisá tu conexión e intentá de nuevo.'
}

const CANDLE_CLOSES = CANDLES.map((candle) => candle.c)
const CANDLE_X = CANDLES.map((_, i) => i * CANDLE_SLOT + CANDLE_SLOT / 2)
const MA_FAST_PATH = linePath(movingAverage(CANDLE_CLOSES, 4).map((y, i) => ({ x: CANDLE_X[i] ?? 0, y })))
const MA_SLOW_PATH = linePath(movingAverage(CANDLE_CLOSES, 9).map((y, i) => ({ x: CANDLE_X[i] ?? 0, y })))

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  //: Sólo existe entre el primer y el segundo factor — null es "todavía no
  //: mandé la contraseña" o "ya terminé", los dos se ven igual acá.
  const [pendingToken, setPendingToken] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState('')
  //: Fuerza un remount del widget (vía `key`) para pedir un token nuevo
  //: después de un intento fallido — los de Turnstile son de un solo uso.
  const [turnstileAttempt, setTurnstileAttempt] = useState(0)
  const [webauthnError, setWebauthnError] = useState<string | null>(null)
  //: "Olvidé mi contraseña" es un tercer estado de esta misma pantalla, no
  //: una ruta aparte — mismo criterio que pendingToken para el paso de 2FA.
  const [forgotMode, setForgotMode] = useState(false)
  const login = useLogin()
  const totpVerify = useTotpVerify()
  const forgotPassword = useForgotPassword()
  const { data: health } = useHealth()
  const webauthnOptions = useWebauthnAuthenticateOptions()
  const webauthnVerify = useWebauthnAuthenticateVerify()

  async function handleUseWebauthn() {
    if (!pendingToken) return
    setWebauthnError(null)
    try {
      const { options } = await webauthnOptions.mutateAsync(pendingToken)
      const credential = await getWebauthnCredential(options)
      await webauthnVerify.mutateAsync({ pending_token: pendingToken, credential })
    } catch (error) {
      setWebauthnError(error instanceof Error ? error.message : 'No se pudo verificar la llave')
    }
  }

  function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    login.mutate(
      { email, password, turnstile_token: turnstileToken },
      {
        onError: () => {
          // El widget se remonta (nuevo `key`) y va a pedir un token nuevo,
          // pero sin limpiar `turnstileToken` acá el botón "Ingresar" seguía
          // habilitado con el token VIEJO (ya usado, Turnstile es de un solo
          // uso) hasta que el widget nuevo terminara de resolver — un
          // segundo intento con la contraseña correcta podía rebotar igual
          // por un token de captcha stale, sin que tuviera nada que ver con
          // la contraseña.
          setTurnstileToken('')
          setTurnstileAttempt((n) => n + 1)
        },
        onSuccess: (result) => {
          if (result.status === 'totp_required') setPendingToken(result.pending_token)
        },
      },
    )
  }

  function handleCodeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!pendingToken) return
    totpVerify.mutate({ pending_token: pendingToken, code })
  }

  function handleForgotSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    forgotPassword.mutate(email)
  }

  return (
    <div className="login-shell">
      <div className="login-shell__atmosphere">
        {/* Velas japonesas decorativas al pie del panel — puramente
            ornamental, sin contenido, por eso aria-hidden. Dos medias
            móviles (rápida y lenta) sobre los cierres, como en cualquier
            gráfico de verdad. */}
        <svg
          className="login-shell__candles"
          viewBox={`0 0 ${CANDLE_CHART_WIDTH} ${CANDLE_CHART_HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {CANDLES.map((candle, i) => {
            const x = CANDLE_X[i] ?? 0
            const up = candle.c < candle.o
            const bodyTop = Math.min(candle.o, candle.c)
            const bodyBottom = Math.max(candle.o, candle.c)
            return (
              <g
                key={x}
                className={`login-shell__candle${up ? ' login-shell__candle--up' : ' login-shell__candle--down'}`}
              >
                <line x1={x} x2={x} y1={candle.h} y2={candle.l} />
                <rect
                  x={x - CANDLE_BODY_WIDTH / 2}
                  y={bodyTop}
                  width={CANDLE_BODY_WIDTH}
                  height={Math.max(2, bodyBottom - bodyTop)}
                />
              </g>
            )
          })}
          <path className="login-shell__ma login-shell__ma--slow" d={MA_SLOW_PATH} />
          <path className="login-shell__ma login-shell__ma--fast" d={MA_FAST_PATH} />
        </svg>

        {/* Anclado arriba a la izquierda, no en el centro con el resto —
            es la marca de la página, no parte del "stage" de fichas +
            texto que sí queda centrado más abajo. */}
        <div className="login-shell__brand">
          <BrandLogo variant="horizontal" className="login-shell__brand-logo" />
        </div>

        {/* Las fichas se posicionan en % contra este "stage", no contra
            todo el panel — así quedan agrupadas alrededor del texto en
            cualquier ancho de ventana, en vez de dispersarse por toda la
            pantalla en un monitor grande. */}
        <div className="login-shell__stage">
          <span className="login-shell__chip login-shell__chip--1 mono">
            BTC <span className="is-up">▲ +2,34%</span>
          </span>
          <span className="login-shell__chip login-shell__chip--2 mono">
            ETH <span className="is-up">▲ +5,12%</span>
          </span>
          <span className="login-shell__chip login-shell__chip--3 mono">
            AAPL <span className="is-down">▼ -0,87%</span>
          </span>
          <span className="login-shell__chip login-shell__chip--4 mono">
            AL41 <span className="is-up">▲ +1,20%</span>
          </span>

          <div className="login-shell__lockup">
            <h2 className="login-shell__headline">Estrategias + Trading Algorítmico</h2>
            <p>Backtesting y operación de bots, todo en un solo lugar.</p>
            <div className="login-shell__pills">
              {FEATURE_PILLS.map((pill) => (
                <span key={pill} className="login-shell__pill">
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="login-shell__panel">
        <ThemeToggle className="login-shell__theme-toggle" />

        {pendingToken ? (
          <form className="login-shell__form" onSubmit={handleCodeSubmit}>
            <BrandLogo variant="horizontal" className="login-shell__form-mark" />
            <p className="card__subtitle">Código de verificación en dos pasos</p>

            {(totpVerify.isError || webauthnError) && (
              <div className="alert" role="alert">
                {webauthnError ?? totpVerify.error?.message}
              </div>
            )}

            <div className="field">
              <label htmlFor="code">Código de tu app de autenticación</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                placeholder="123456"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
              <p className="muted field__hint">
                Sin acceso al dispositivo, se puede usar uno de los códigos de recuperación en su
                lugar.
              </p>
            </div>

            <button className="button" type="submit" disabled={totpVerify.isPending}>
              {totpVerify.isPending ? 'Verificando…' : 'Verificar'}
            </button>
            {health?.webauthn_available && isWebauthnSupported() && (
              <button
                className="button button--ghost"
                type="button"
                disabled={webauthnOptions.isPending || webauthnVerify.isPending}
                onClick={handleUseWebauthn}
              >
                {webauthnOptions.isPending || webauthnVerify.isPending
                  ? 'Verificando…'
                  : 'Usar llave de acceso'}
              </button>
            )}
            <button
              className="link-button login-shell__back"
              type="button"
              onClick={() => {
                setPendingToken(null)
                setCode('')
              }}
            >
              Volver
            </button>
          </form>
        ) : forgotMode ? (
          <form className="login-shell__form" onSubmit={handleForgotSubmit}>
            <BrandLogo variant="horizontal" className="login-shell__form-mark" />
            <h1 className="login-shell__greeting">Restablecer contraseña</h1>

            {forgotPassword.isSuccess ? (
              <>
                <p className="card__subtitle">{forgotPassword.data.message}</p>
                <button
                  className="link-button login-shell__back"
                  type="button"
                  onClick={() => setForgotMode(false)}
                >
                  Volver
                </button>
              </>
            ) : (
              <>
                <p className="card__subtitle">
                  Escribí tu email — si tiene una cuenta, te mandamos un link para elegir una
                  contraseña nueva.
                </p>

                {forgotPassword.isError && (
                  <div className="alert" role="alert">
                    {loginErrorMessage(forgotPassword.error)}
                  </div>
                )}

                <div className="field">
                  <label htmlFor="forgot-email">Email</label>
                  <input
                    id="forgot-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    placeholder="tu@email.com"
                    autoFocus
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>

                <button className="button" type="submit" disabled={forgotPassword.isPending}>
                  {forgotPassword.isPending ? 'Mandando…' : 'Mandar link'}
                </button>
                <button
                  className="link-button login-shell__back"
                  type="button"
                  onClick={() => setForgotMode(false)}
                >
                  Volver
                </button>
              </>
            )}
          </form>
        ) : (
          <form className="login-shell__form" onSubmit={handlePasswordSubmit}>
            <BrandLogo variant="horizontal" className="login-shell__form-mark" />
            <h1 className="login-shell__greeting">Bienvenido</h1>
            <p className="card__subtitle">Iniciá sesión para continuar</p>

            {login.isError && (
              <div className="alert" role="alert">
                {loginErrorMessage(login.error)}
              </div>
            )}

            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder="tu@email.com"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <PasswordField
              id="password"
              label="Contraseña"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
              labelAction={
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    forgotPassword.reset()
                    setForgotMode(true)
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              }
            />

            {turnstileSiteKey && (
              <TurnstileWidget
                key={turnstileAttempt}
                siteKey={turnstileSiteKey}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
              />
            )}

            <button
              className="button login-shell__submit"
              type="submit"
              disabled={login.isPending || (Boolean(turnstileSiteKey) && !turnstileToken)}
            >
              <LogIn size={16} />
              {login.isPending ? 'Ingresando…' : 'Ingresar'}
            </button>

            <p className="login-shell__footer muted">© {new Date().getFullYear()} Deltabot</p>
          </form>
        )}
      </div>
    </div>
  )
}
